import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Loader2, Save } from "lucide-react";
import {
    adminGetGlobalSettings,
    adminUpdateGlobalSettings,
} from "../../lib/api";

/**
 * Admin → Settings tab (iter-112).
 *
 * Holds site-wide knobs the superadmin can tweak. First knob: per-user
 * storage cap (in MB). Set to 0 to disable the cap entirely.
 *
 * Storage enforcement itself will be wired into the upload pipeline in
 * a follow-up — this tab is the source of truth for the limit.
 */
export default function SettingsTab() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [limitMb, setLimitMb] = useState("");

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const s = await adminGetGlobalSettings();
                if (dead) return;
                setLimitMb(
                    s.user_storage_limit_mb
                        ? String(s.user_storage_limit_mb)
                        : "",
                );
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "Couldn't load settings",
                );
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => { dead = true; };
    }, []);

    const save = async () => {
        // Accept blank as "unlimited" (stored as 0).
        const trimmed = (limitMb || "").trim();
        const next = trimmed === "" ? 0 : Number(trimmed);
        if (!Number.isFinite(next) || next < 0) {
            toast.error("Storage limit must be a non-negative number of MB");
            return;
        }
        setSaving(true);
        try {
            const res = await adminUpdateGlobalSettings({
                user_storage_limit_mb: next,
            });
            setLimitMb(
                res.user_storage_limit_mb
                    ? String(res.user_storage_limit_mb)
                    : "",
            );
            toast.success(
                next === 0
                    ? "Storage cap disabled"
                    : `Per-user storage cap set to ${next} MB`,
            );
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't save",
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
            </div>
        );
    }

    const gbEquiv = limitMb && Number(limitMb) > 0
        ? (Number(limitMb) / 1024).toFixed(2)
        : null;

    return (
        <div className="space-y-6 max-w-2xl" data-testid="admin-settings-tab">
            <section
                className="bg-[#0A1128] border border-[#2A3655] rounded-md p-5 space-y-3"
                data-testid="setting-storage-limit"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/40 flex items-center justify-center">
                        <HardDrive className="w-5 h-5 text-[#00A3FF]" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base font-bold text-white">Per-user storage limit</div>
                        <div className="text-xs text-slate-400">
                            Applies to every file a user uploads — PDFs, images, attachments, anything stored on their behalf.
                            Leave blank or 0 for unlimited.
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <div className="relative flex-1">
                        <input
                            type="number"
                            min="0"
                            step="1"
                            value={limitMb}
                            onChange={(e) => setLimitMb(e.target.value)}
                            placeholder="e.g. 5000 for 5 GB (blank = unlimited)"
                            data-testid="input-storage-limit-mb"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 pr-12 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF]"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-wider text-slate-500 font-bold pointer-events-none">
                            MB
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="btn-save-storage-limit"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#00A3FF] hover:bg-[#3395FF] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                    </button>
                </div>
                {gbEquiv && (
                    <div className="text-[11px] text-slate-500">
                        ≈ {gbEquiv} GB per user.
                    </div>
                )}
                {(!limitMb || Number(limitMb) === 0) && (
                    <div className="text-[11px] text-emerald-400/80 font-bold uppercase tracking-wider">
                        Unlimited
                    </div>
                )}
            </section>
        </div>
    );
}
